import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type {
  EmailSettingsDto,
  SaveEmailSettingsRequest,
  SendTestEmailResult,
} from "@procureflow/shared-types";
import { PageHeader } from "@/components/page-header";
import { fetchWithAuth } from "@/lib/auth-client";
import { useAuth } from "@/contexts/auth-context";

// US-073 — Admin Email (SMTP) settings. Admin → editable form + Send-test;
// everyone else → access-denied panel (the API enforces 403 regardless).

export const Route = createFileRoute("/settings/email")({
  head: () => ({
    meta: [
      { title: "Email Settings — ProcureFlow" },
      { name: "description", content: "Configure SMTP delivery for approval notifications." },
    ],
  }),
  component: EmailSettingsPage,
});

type FetchState =
  | { kind: "loading" }
  | { kind: "loaded"; data: EmailSettingsDto }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

function EmailSettingsPage() {
  const { user } = useAuth();
  const [state, setState] = useState<FetchState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      try {
        const res = await fetchWithAuth("/api/email-settings");
        if (cancelled) return;
        if (res.status === 403) {
          setState({ kind: "forbidden" });
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setState({ kind: "error", message: body.message ?? "Failed to load settings." });
          return;
        }
        const { data } = (await res.json()) as { data: EmailSettingsDto };
        if (!cancelled) setState({ kind: "loaded", data });
      } catch (err) {
        if (!cancelled)
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "Unexpected error.",
          });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="space-y-6" data-testid="email-settings-section">
        <PageHeader title="Email Settings" description="Loading…" />
      </div>
    );
  }
  if (state.kind === "forbidden") {
    return (
      <div className="space-y-6" data-testid="email-settings-section">
        <PageHeader title="Email Settings" description="You don't have access to email settings." />
        <div
          className="rounded-2xl bg-card border border-border p-10 text-sm text-muted-foreground shadow-elegant"
          data-testid="email-settings-forbidden"
        >
          This page is restricted to Admins.
        </div>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="space-y-6" data-testid="email-settings-section">
        <PageHeader title="Email Settings" description="Something went wrong." />
        <div className="rounded-2xl bg-card border border-border p-10 text-sm text-destructive shadow-elegant">
          {state.message}
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === "Admin";
  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="email-settings-section">
        <PageHeader title="Email Settings" description="You don't have access to email settings." />
        <div
          className="rounded-2xl bg-card border border-border p-10 text-sm text-muted-foreground shadow-elegant"
          data-testid="email-settings-forbidden"
        >
          This page is restricted to Admins.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-section">
      <PageHeader
        title="Email Settings"
        description="Configure SMTP so approval notifications are delivered by email."
      />
      <EmailSettingsForm
        initial={state.data}
        onSaved={(data) => setState({ kind: "loaded", data })}
      />
    </div>
  );
}

function EmailSettingsForm({
  initial,
  onSaved,
}: {
  initial: EmailSettingsDto;
  onSaved: (next: EmailSettingsDto) => void;
}) {
  const [host, setHost] = useState(initial.host ?? "");
  const [port, setPort] = useState(initial.port != null ? String(initial.port) : "587");
  const [secure, setSecure] = useState(initial.secure);
  const [username, setUsername] = useState(initial.username ?? "");
  const [password, setPassword] = useState("");
  const [fromAddress, setFromAddress] = useState(initial.from_address ?? "");
  const [fromName, setFromName] = useState(initial.from_name ?? "");
  const [hasPassword, setHasPassword] = useState(initial.has_password);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testState, setTestState] = useState<{ kind: "idle" | "testing" } | SendTestEmailResult>({
    kind: "idle",
  });

  async function handleSubmit() {
    setError(null);
    setToast(null);
    const portNum = Number(port);
    if (!host.trim() || !Number.isInteger(portNum) || portNum <= 0 || !fromAddress.trim()) {
      setError("Host, a valid port, and From address are required.");
      return;
    }
    setSubmitting(true);
    try {
      const body: SaveEmailSettingsRequest = {
        host: host.trim(),
        port: portNum,
        secure,
        username: username.trim() || null,
        from_address: fromAddress.trim(),
        from_name: fromName.trim() || null,
        ...(password ? { password } : {}),
      };
      const res = await fetchWithAuth("/api/email-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const respBody = (await res.json().catch(() => ({}))) as {
        data?: EmailSettingsDto;
        message?: string;
      };
      if (!res.ok) {
        setError(respBody.message ?? "Could not save settings.");
        return;
      }
      if (respBody.data) {
        onSaved(respBody.data);
        setHasPassword(respBody.data.has_password);
        setPassword("");
        setToast("Email settings saved.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unexpected error.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSendTest() {
    setTestState({ kind: "testing" });
    try {
      const res = await fetchWithAuth("/api/email-settings/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const respBody = (await res.json().catch(() => ({}))) as { data?: SendTestEmailResult };
      setTestState(respBody.data ?? { ok: false, error: "No response." });
    } catch (err) {
      setTestState({ ok: false, error: err instanceof Error ? err.message : "Unexpected error." });
    }
  }

  return (
    <form
      data-testid="email-settings-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!submitting) void handleSubmit();
      }}
      className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-5"
    >
      {toast && (
        <div
          data-testid="email-settings-toast"
          className="rounded-lg border border-border bg-success/10 px-3 py-2 text-xs [color:var(--success)]"
        >
          {toast}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
        <Field id="smtp-host" label="SMTP host">
          <Input id="smtp-host" value={host} onChange={setHost} placeholder="smtp.example.com" />
        </Field>
        <Field id="smtp-port" label="Port">
          <Input id="smtp-port" type="number" value={port} onChange={setPort} />
        </Field>
        <Field id="smtp-username" label="Username">
          <Input id="smtp-username" value={username} onChange={setUsername} />
        </Field>
        <Field
          id="smtp-password"
          label="Password"
          hint={hasPassword ? "A password is stored. Leave blank to keep it." : undefined}
        >
          <Input
            id="smtp-password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder={hasPassword ? "••••••••" : ""}
          />
        </Field>
        <Field id="from-address" label="From address">
          <Input
            id="from-address"
            value={fromAddress}
            onChange={setFromAddress}
            placeholder="noreply@example.com"
          />
        </Field>
        <Field id="from-name" label="From name">
          <Input id="from-name" value={fromName} onChange={setFromName} placeholder="ProcureFlow" />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          id="smtp-secure"
          type="checkbox"
          checked={secure}
          onChange={(e) => setSecure(e.target.checked)}
        />
        Use TLS/SSL (secure connection)
      </label>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="send-test-email"
            onClick={() => void handleSendTest()}
            disabled={testState !== null && "kind" in testState && testState.kind === "testing"}
            className="inline-flex items-center rounded-lg border border-border px-3.5 py-1.5 text-sm font-medium disabled:opacity-50"
          >
            {"kind" in testState && testState.kind === "testing" ? "Sending…" : "Send test email"}
          </button>
          {"ok" in testState &&
            (testState.ok ? (
              <span data-testid="test-result-ok" className="text-xs [color:var(--success)]">
                Test email sent.
              </span>
            ) : (
              <span data-testid="test-result-error" className="text-xs text-destructive">
                {testState.error ?? "Test failed."}
              </span>
            ))}
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </form>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-foreground">
        {label}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Input({
  id,
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  id: string;
  label?: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <input
      id={id}
      aria-label={label}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
    />
  );
}
